import React from "react";
import { useLang } from "../lib/i18n";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const EMPTY_TERMS = {
  borrower_kind: "individual", item_use: "personal", penalty_mode: "penalty_clause", penalty_rate: 10, late_rate: "",
  storage_cost: "", storage_period: "month", insurance_contribution: "", other_fixed_costs: "", capital_delivery: "",
  payment_schedule: "", witness_name: "", witness_id: "", refund_account: "", guarantor: null, consenter: null,
};
const EMPTY_PARTY = { name: "", id_number: "", nif: "", role: "", share_percent: "" };

export function termsPayload(t) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const party = (p) => (p && p.name ? { ...p, share_percent: num(p.share_percent) } : null);
  return {
    borrower_kind: t.borrower_kind, item_use: t.item_use, penalty_mode: t.penalty_mode,
    penalty_rate: num(t.penalty_rate), late_rate: num(t.late_rate), storage_cost: num(t.storage_cost),
    storage_period: t.storage_period, insurance_contribution: num(t.insurance_contribution),
    other_fixed_costs: t.other_fixed_costs || null, capital_delivery: t.capital_delivery || null,
    payment_schedule: t.payment_schedule || null, witness_name: t.witness_name || null, witness_id: t.witness_id || null,
    refund_account: t.refund_account || null, guarantor: party(t.guarantor), consenter: party(t.consenter),
  };
}

export function termsFromLoan(l) {
  const s = (v) => (v == null ? "" : v);
  return { ...EMPTY_TERMS, ...Object.fromEntries(Object.keys(EMPTY_TERMS).map((k) => [k, k === "guarantor" || k === "consenter" ? l[k] || null : s(l[k] ?? EMPTY_TERMS[k])])) };
}

function PartyBlock({ label, value, onChange, testid }) {
  const { t } = useLang();
  const set = (k, v) => onChange({ ...(value || EMPTY_PARTY), [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testid}-toggle`}
          onClick={() => onChange(value ? null : { ...EMPTY_PARTY })}>{value ? t("ct.remove") : t("ct.add")}</Button>
      </div>
      {value && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
          <Input data-testid={`${testid}-name`} placeholder={t("ct.party_name")} value={value.name} onChange={(e) => set("name", e.target.value)} className="h-8 text-xs col-span-2 sm:col-span-1" />
          <Input placeholder={t("ct.party_id")} value={value.id_number || ""} onChange={(e) => set("id_number", e.target.value)} className="h-8 text-xs" />
          <Input placeholder={t("ct.party_nif")} value={value.nif || ""} onChange={(e) => set("nif", e.target.value)} className="h-8 text-xs" />
          <Input placeholder={t("ct.party_role")} value={value.role || ""} onChange={(e) => set("role", e.target.value)} className="h-8 text-xs" />
          <Input type="number" placeholder={t("ct.party_share")} value={value.share_percent ?? ""} onChange={(e) => set("share_percent", e.target.value)} className="h-8 text-xs" />
        </div>
      )}
    </div>
  );
}

export function ContractTermsFields({ value, onChange }) {
  const { t } = useLang();
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3 border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid="contract-terms-fields">
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("ct.section")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">{t("ct.borrower_kind")}</Label>
          <Select value={value.borrower_kind} onValueChange={(v) => set("borrower_kind", v)}>
            <SelectTrigger data-testid="terms-borrower-kind" className="mt-1 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="individual">{t("ct.individual")}</SelectItem>
              <SelectItem value="company">{t("ct.company")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("ct.item_use")}</Label>
          <Select value={value.item_use} onValueChange={(v) => set("item_use", v)}>
            <SelectTrigger data-testid="terms-item-use" className="mt-1 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="personal" data-testid="terms-use-personal">{t("ct.use_personal")}</SelectItem>
              <SelectItem value="professional" data-testid="terms-use-professional">{t("ct.use_professional")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("ct.penalty_mode")}</Label>
          <Select value={value.penalty_mode} onValueChange={(v) => set("penalty_mode", v)}>
            <SelectTrigger data-testid="terms-penalty-mode" className="mt-1 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="penalty_clause">{t("ct.penalty_clause")}</SelectItem>
              <SelectItem value="late_interest">{t("ct.late_interest")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {value.penalty_mode === "penalty_clause" ? (
          <div>
            <Label className="text-xs">{t("ct.penalty_rate")}</Label>
            <Input data-testid="terms-penalty-rate" type="number" min="0" max="100" step="0.1" value={value.penalty_rate} onChange={(e) => set("penalty_rate", e.target.value)} className="mt-1 h-9 bg-white" />
          </div>
        ) : (
          <div>
            <Label className="text-xs">{t("ct.late_rate")}</Label>
            <Input data-testid="terms-late-rate" type="number" min="0" max="100" step="0.1" value={value.late_rate} onChange={(e) => set("late_rate", e.target.value)} className="mt-1 h-9 bg-white" />
          </div>
        )}
        <div className="flex gap-2">
          <div className="flex-1">
            <Label className="text-xs">{t("ct.storage_cost")}</Label>
            <Input data-testid="terms-storage-cost" type="number" min="0" step="0.01" value={value.storage_cost} onChange={(e) => set("storage_cost", e.target.value)} className="mt-1 h-9 bg-white" />
          </div>
          <div className="w-24">
            <Label className="text-xs">{t("ct.storage_period")}</Label>
            <Select value={value.storage_period} onValueChange={(v) => set("storage_period", v)}>
              <SelectTrigger className="mt-1 h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["day", "week", "month"].map((p) => <SelectItem key={p} value={p}>{t(`ct.${p}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">{t("ct.insurance")}</Label>
          <Input data-testid="terms-insurance" type="number" min="0" step="0.01" value={value.insurance_contribution} onChange={(e) => set("insurance_contribution", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div className="col-span-2 sm:col-span-3">
          <Label className="text-xs">{t("ct.other_costs")}</Label>
          <Input value={value.other_fixed_costs} onChange={(e) => set("other_fixed_costs", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">{t("ct.capital_delivery")}</Label>
          <Input value={value.capital_delivery} onChange={(e) => set("capital_delivery", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div className="col-span-2 sm:col-span-2">
          <Label className="text-xs">{t("ct.payment_schedule")}</Label>
          <Input value={value.payment_schedule} onChange={(e) => set("payment_schedule", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div>
          <Label className="text-xs">{t("ct.witness")}</Label>
          <Input data-testid="terms-witness" value={value.witness_name} onChange={(e) => set("witness_name", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div>
          <Label className="text-xs">{t("ct.witness_id")}</Label>
          <Input value={value.witness_id} onChange={(e) => set("witness_id", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
        <div>
          <Label className="text-xs">{t("ct.refund_account")}</Label>
          <Input value={value.refund_account} onChange={(e) => set("refund_account", e.target.value)} className="mt-1 h-9 bg-white" />
        </div>
      </div>
      <PartyBlock label={t("ct.guarantor")} value={value.guarantor} onChange={(v) => set("guarantor", v)} testid="terms-guarantor" />
      <PartyBlock label={t("ct.consenter")} value={value.consenter} onChange={(v) => set("consenter", v)} testid="terms-consenter" />
      <p className="text-[11px] text-slate-500">{t("ct.printed_only")}</p>
    </div>
  );
}
